import { useInfiniteQuery } from '@tanstack/react-query';

import { TableFilterState } from '@/components/data-table/filters-v2';
import { DEFAULT_PAGE_SIZE, getNextPageParam } from '@/lib/constants/pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { getTimeOffRequestsToReview } from '../api/time-offs.api';
import { getTimeOffRequestsToReviewQueryKey } from '../constants/time-off-approvals-query-keys';
import { mapTimeOffFilters } from '../mappers/time-off-filters.mapper';
import { TimeOffRequest } from '../types/time-off-request';

// Cache the list so reopening the approvals screen doesn't refetch; mutations invalidate it.
const TO_REVIEW_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Infinite query for time-off requests the current user can review (approve/decline).
 * Mirrors {@link useTimeOffRequests} but hits the to-review endpoint.
 */
export const useTimeOffRequestsToReview = (filters: TableFilterState) => {
  return useInfiniteQuery({
    queryKey: getTimeOffRequestsToReviewQueryKey(filters),
    queryFn: ({ pageParam }) =>
      getTimeOffRequestsToReview({
        filter: mapTimeOffFilters(filters),
        pagination: { cursor: pageParam, pageSize: DEFAULT_PAGE_SIZE },
      }),
    initialPageParam: null as number | null,
    staleTime: TO_REVIEW_STALE_TIME,
    getNextPageParam,
    select: data =>
      new PaginatedResponse<TimeOffRequest>({
        items: data.pages.flatMap(page => page.items),
        pagination: data.pages[data.pages.length - 1]?.pagination ?? { total: 0, cursor: null },
      }),
  });
};
